import cn from 'classnames';
import { FC, ReactNode, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import type { Document, TranscriptChunk } from '@/shared/api/transcriptor';
import { Badge, Button } from '@/shared/ui';

import {
  isBlockNoteTemplate,
  type DocumentTemplate,
} from '@/entities/templates';
import { transcriptorMessages } from '@/entities/transcriptor';

import {
  type AnswerEdit,
  type LiveDocumentState,
} from '@/features/audioRecording';

import type { EncounterHeading } from '../config';
import {
  useCanEditTemplates,
  useDocumentTemplate,
  useNoteLists,
  useStoredDocumentAnswers,
} from '../hooks';
import {
  applyPendingAnswers,
  applySectionOverrides,
  documentAnswersToDraft,
  getMissingRequiredSections,
  getTemplateOrigin,
  toAnswerEdit,
  type UnresolvedNoteLine,
} from '../lib';

import { DocumentHeader } from './DocumentRow';
import { OpenTemplateButton } from './EditTemplateButton';
import styles from './LiveProtocolDocument.module.scss';
import type { NoteAssumptionsReport } from './NoteAssumptions';
import { NoteDocument } from './NoteDocument';
import { CatalogOriginLabel } from './WorkspaceTemplateCatalog';

// The server accepts edits during recording and after processing completes.
export type ProtocolPhase = 'recording' | 'processing' | 'done';

type Props = {
  document: Document;
  phase: ProtocolPhase;
  state: LiveDocumentState | undefined;
  chunks: TranscriptChunk[];
  canEdit: boolean;
  // Off when the protocol is filled only after Stop.
  fillsLive?: boolean;
  className?: string;
  // Without it the answers are shown read-only.
  onAnswer?: (template: DocumentTemplate, key: string, value: string) => void;
  // Hands a manual answer back to the live fill, so only while recording.
  onRelease?: (template: DocumentTemplate, key: string) => void;
  // Titles of the required sections without an answer, reported while the
  // finished document is shown.
  onMissingRequiredChange?: (titles: string[]) => void;
  // Block notes: operations go to scribe as they are, and the lines left
  // unresolved and the values AI assumed hold "Save to record".
  onNoteEdit?: (edit: AnswerEdit) => void;
  onUnresolvedNoteLinesChange?: (lines: UnresolvedNoteLine[]) => void;
  onNoteAssumptionsChange?: (report: NoteAssumptionsReport) => void;
  // Answers another request stored, e.g. "Complete with AI".
  // Block notes are laid out from the server's view, read before any fill.
  onReadAnswers?: () => void;
  encounterHeading?: EncounterHeading;
};

const PHASE_BADGES: Record<ProtocolPhase, ReactNode> = {
  recording: (
    <Badge size="small" tone="violet">
      <FormattedMessage id="workspace.live.badge" defaultMessage="Live" />
    </Badge>
  ),
  processing: (
    <Badge size="small" tone="violet">
      <FormattedMessage {...transcriptorMessages.statusProcessing} />
    </Badge>
  ),
  done: null,
};

const LiveStatus: FC<{ children: ReactNode }> = ({ children }) => (
  <p role="status" className={cn('p2', styles.status)}>
    {children}
  </p>
);

// A protocol shown as its template filled with structured answers. Answer
// keys point at the template version the encounter pinned, so that version is
// loaded, not the latest.
export const LiveProtocolDocument: FC<Props> = ({
  document,
  phase,
  state,
  chunks,
  canEdit,
  fillsLive = true,
  className,
  onAnswer,
  onRelease,
  onMissingRequiredChange,
  onNoteEdit,
  onUnresolvedNoteLinesChange,
  onNoteAssumptionsChange,
  onReadAnswers,
  encounterHeading,
}) => {
  const {
    template: pinnedTemplate,
    failed,
    retry,
  } = useDocumentTemplate(document.template_id ?? '');
  const template = pinnedTemplate
    ? applySectionOverrides(pinnedTemplate, document.section_overrides)
    : undefined;
  const blockNote = !!template && isBlockNoteTemplate(template);
  const noteLists = useNoteLists(blockNote ? template : undefined);
  const canEditTemplates = useCanEditTemplates();
  const title = document.name || document.tab_title || document.code || '';
  const headerTitle = title === encounterHeading?.title ? undefined : title;
  const phaseBadge =
    phase === 'processing' && encounterHeading?.status === 'processing'
      ? null
      : PHASE_BADGES[phase];
  const answers =
    template && !blockNote
      ? applyPendingAnswers(
          documentAnswersToDraft({
            template,
            snapshot: state?.snapshot,
            chunks,
          }),
          state?.pending ?? {},
        )
      : undefined;
  const missing =
    template && answers
      ? getMissingRequiredSections({ template, ...answers })
      : [];
  // Live answers can be reviewed while the server renders the document.
  const reviewing = phase === 'done' || (phase === 'processing' && fillsLive);
  const reportedKey =
    phase === 'done' && answers
      ? JSON.stringify(missing.map((section) => section.Title))
      : null;
  // The callback is called from an effect keyed on the titles only.
  const onMissingRef = useRef(onMissingRequiredChange);
  onMissingRef.current = onMissingRequiredChange;
  const onReadAnswersRef = useRef(onReadAnswers);
  onReadAnswersRef.current = onReadAnswers;
  const awaitingNote = blockNote && !state?.snapshot?.note;

  useEffect(() => {
    if (reportedKey !== null) {
      onMissingRef.current?.(JSON.parse(reportedKey) as string[]);
    }
  }, [reportedKey]);

  useEffect(() => {
    if (awaitingNote) {
      onReadAnswersRef.current?.();
    }
  }, [awaitingNote]);

  const notices = (
    <>
      {phase === 'recording' && !fillsLive && (
        <LiveStatus>
          <FormattedMessage
            id="workspace.live.fillAfterStop"
            defaultMessage="The template is filled after you stop recording."
          />
        </LiveStatus>
      )}
      {phase === 'recording' && state?.fillFailed && (
        <LiveStatus>
          <FormattedMessage
            id="workspace.live.fillFailed"
            defaultMessage="AI has not applied the latest phrase yet; it tries again with the next one."
          />
        </LiveStatus>
      )}
      {phase === 'processing' && (
        <LiveStatus>
          <FormattedMessage
            id="workspace.live.processingReadOnly"
            defaultMessage="Processing… you can edit when it finishes."
          />
        </LiveStatus>
      )}
      {state?.saveFailed && (
        <p role="alert" className={cn('p3', styles.alert)}>
          <FormattedMessage
            id="workspace.live.saveFailed"
            defaultMessage="Your last change was not saved. The last accepted answers are shown."
          />
        </p>
      )}
    </>
  );

  const header = (!!headerTitle || !!phaseBadge) && (
    <DocumentHeader title={headerTitle}>{phaseBadge}</DocumentHeader>
  );

  const sourceLabel = template && (
    <>
      <CatalogOriginLabel origin={getTemplateOrigin(template)} />
      {template.catalog_metadata?.category &&
        ` · ${template.catalog_metadata.category}`}
    </>
  );

  // A block note's own header names its template, so only the phase is added.
  if (template && blockNote) {
    return (
      <section
        data-component="workspace-live-document"
        className={cn(styles.document, className)}
      >
        {phaseBadge && <DocumentHeader>{phaseBadge}</DocumentHeader>}
        {notices}
        <NoteDocument
          template={template}
          sourceLabel={sourceLabel}
          templateLink={
            canEditTemplates && (
              <OpenTemplateButton seriesId={template.series_id} />
            )
          }
          note={{
            answers: state?.snapshot?.note,
            pending: state?.pending ?? {},
            lists: noteLists.lists,
            listsFailed: noteLists.failed,
            editable: canEdit && phase !== 'processing' && !!onNoteEdit,
            approved: !!document.approved,
            listening: phase === 'recording' && fillsLive,
            reviewing,
            onEdit: (edit) => onNoteEdit?.(edit),
            onUnresolvedChange: onUnresolvedNoteLinesChange,
            onMissingRequiredChange,
            onAssumptionsChange: onNoteAssumptionsChange,
          }}
        />
      </section>
    );
  }

  if (failed || !template || !answers) {
    return (
      <section className={cn(styles.document, className)}>
        {headerTitle && <DocumentHeader title={headerTitle} />}
        {failed ? (
          <Button variant="secondary" size="medium" onClick={retry}>
            <FormattedMessage
              id="workspace.retryTemplate"
              defaultMessage="Template could not be loaded. Retry"
            />
          </Button>
        ) : (
          <p className={cn('p3', styles.notice)}>
            <FormattedMessage id="global.loading" defaultMessage="Loading…" />
          </p>
        )}
      </section>
    );
  }

  const editable = canEdit && phase !== 'processing' && !!onAnswer;

  return (
    <section
      data-component="workspace-live-document"
      className={cn(styles.document, className)}
    >
      {header}
      {notices}
      {answers.unplaced > 0 && (
        <LiveStatus>
          <FormattedMessage
            id="workspace.live.unplaced"
            defaultMessage="Answers that do not match this template version: {count}. They are not shown."
            values={{ count: answers.unplaced }}
          />
        </LiveStatus>
      )}
      <NoteDocument
        template={template}
        sourceLabel={sourceLabel}
        disabled={!editable}
        options={{
          draft: answers,
          live: true,
          missingRequired: reviewing ? missing : undefined,
          onAnswer: (key, value) => {
            if (editable) {
              onAnswer?.(template, key, value);
            }
          },
          onRelease:
            phase === 'recording' && onRelease
              ? (key) => onRelease(template, key)
              : undefined,
        }}
      />
    </section>
  );
};

type StoredProps = {
  encounterId: string;
  document: Document;
  phase: ProtocolPhase;
  canEdit: boolean;
  // Shown when the document has no stored answers, e.g. text-only results.
  fallback: ReactNode;
  className?: string;
  onSaved?: () => void;
  onMissingRequiredChange?: (titles: string[]) => void;
  onUnresolvedNoteLinesChange?: (lines: UnresolvedNoteLine[]) => void;
  onNoteAssumptionsChange?: (report: NoteAssumptionsReport) => void;
  encounterHeading?: EncounterHeading;
};

export const StoredProtocolDocument: FC<StoredProps> = ({
  encounterId,
  document,
  phase,
  canEdit,
  fallback,
  className,
  onSaved,
  onMissingRequiredChange,
  onUnresolvedNoteLinesChange,
  onNoteAssumptionsChange,
  encounterHeading,
}) => {
  const { state, edit } = useStoredDocumentAnswers({
    encounterId,
    documentId: document.id ?? '',
    revisionKey: document.status ?? '',
    onSaved,
  });

  if (!state) {
    return (
      <p className={cn('p3', styles.notice, className)}>
        <FormattedMessage id="global.loading" defaultMessage="Loading…" />
      </p>
    );
  }
  const answers = state.snapshot;
  if (
    !answers ||
    (!answers.note && !answers.answers.length && !answers.notes.length)
  ) {
    return <>{fallback}</>;
  }
  return (
    <LiveProtocolDocument
      className={className}
      document={document}
      phase={phase}
      state={{ ...state, fillFailed: false, finalFailed: false }}
      chunks={[]}
      // The stored answers are read, not followed as they are filled.
      fillsLive={false}
      canEdit={canEdit}
      onMissingRequiredChange={onMissingRequiredChange}
      onUnresolvedNoteLinesChange={onUnresolvedNoteLinesChange}
      onNoteAssumptionsChange={onNoteAssumptionsChange}
      onAnswer={(template, key, value) => {
        const answerEdit = toAnswerEdit({ template, key, value });
        if (answerEdit) {
          edit(answerEdit);
        }
      }}
      onNoteEdit={edit}
      encounterHeading={encounterHeading}
    />
  );
};
